import { LoadingFallback } from 'components/common';
import useMergeState from 'hooks/useMergeState';
import { Profile } from 'models';
import moment from 'moment';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { matchPath, RouteObject, useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'routes';
import { accountService } from 'services';
import {
  getAccessToken,
  getAuthenticationResult,
  getRefreshToken,
  removeToken,
  setAccessToken,
  setToken,
} from 'services/token.service';
import { AppRouteType, ResponseForgot, ResponseReset, AuthenticationResult, SuccessResponse } from 'types';

interface AuthContextType {
  loading: boolean;
  accessToken: null | string | undefined;
  isAuth: boolean;
  profile: Profile | null;
  onSignIn: (credentials: { email: string; password: string; remember: boolean }) => Promise<void>;
  onSignOut: () => void;
  onRefreshToken: () => Promise<string | null>;
  getProfile: () => void;
  onForgot: (credentials: { email: string; frontendUrl: string }) => Promise<ResponseForgot>;
  onVerify: (credentials: { token: string }) => Promise<ResponseReset>;
  onResetPass: (credentials: {
    email: string;
    password: string;
    token: string;
    frontendUrl: string;
  }) => Promise<ResponseReset>;
}

const AuthContext = createContext<AuthContextType>({
  loading: false,
  accessToken: null,
  isAuth: false,
  profile: null,
  onSignIn: async () => Promise.resolve(),
  onSignOut: () => {},
  onRefreshToken: async () => Promise.resolve(null),
  getProfile: () => {},
  onForgot: async () => Promise.resolve({ statusCode: 500, message: 'Default error', success: true }),
  onVerify: async () => Promise.resolve({ email: '', success: true }),
  onResetPass: async () => Promise.resolve({ email: '', success: true }),
});

let refreshPromise: Promise<string | null> | null = null;

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const tokenStorage = getAuthenticationResult();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRY = 5;

  const [userData, setUserData] = useMergeState<{
    isFirstLoading: boolean;
    loading: boolean;
    accessToken: string | null;
    isAuth: boolean;
    profile: Profile | null;
    remember?: boolean;
  }>({
    isFirstLoading: true,
    loading: false,
    accessToken: tokenStorage?.accessToken || null,
    isAuth: false,
    profile: tokenStorage?.profile || null,
    remember: false,
  });

  const currentPage = routes?.find((route: RouteObject) => {
    if (route.path !== '*' && route.path && matchPath(route.path, location.pathname)) {
      return true;
    }
    if (route.path === '*') {
      return !routes.some((r) => r.path !== '*' && matchPath(r.path || '', location.pathname));
    }
  }) as AppRouteType;

  // ================================
  // 🔑 AUTH ACTIONS
  // ================================
  const onSignIn = async (credentials: { email: string; password: string; remember: boolean }) => {
    try {
      setUserData({ loading: true });
      const response = await accountService.signIn(credentials);
      const authenticationResult = response?.data as unknown as SuccessResponse;

      if (authenticationResult?.success) {
        const rs = authenticationResult?.data as unknown as AuthenticationResult;
        setToken({ ...rs, remember: credentials.remember });
        setUserData({
          accessToken: rs.accessToken,
          profile: rs.profile,
          loading: false,
          isFirstLoading: false,
        });
      }
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };

  const onSignOut = async () => {
    try {
      if (userData?.profile) {
        const accessToken = getAccessToken();
        await accountService.signOut({ accessToken });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUserData({
        isFirstLoading: false,
        loading: false,
        accessToken: null,
        isAuth: false,
        profile: null,
      });
      removeToken();
      navigate('/login', { replace: true });
    }
  };

  // ================================
  // 🔁 REFRESH TOKEN
  // ================================
  const onRefreshToken = async (refreshTokenData?: string): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;
    setIsRefreshing(true);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    refreshPromise = (async () => {
      try {
        const refreshToken = getRefreshToken() ?? refreshTokenData;
        if (!refreshToken) throw new Error('Missing refresh token');

        const response = await accountService.refreshToken(refreshToken);
        const newAccessToken = (response?.data as unknown as { data: { accessToken: string } })?.data?.accessToken;
        if (!newAccessToken) throw new Error('Invalid access token');

        setAccessToken(newAccessToken);
        setUserData({ accessToken: newAccessToken });
        console.log('✅ Access token refreshed');
        return newAccessToken;
      } catch (error) {
        if (
          (error as { name: string })?.name === 'CanceledError' ||
          (error as { message: string }).message === 'canceled'
        ) {
          console.warn('⚠️ Refresh token request was canceled.');
          return null;
        }
        console.error('❌ Refresh token failed:', error);
        await onSignOut();
        return null;
      } finally {
        setIsRefreshing(false);
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  // ================================
  // 👤 PROFILE
  // ================================
  const getProfile = async (tokenStorage?: AuthenticationResult) => {
    try {
      setUserData({ loading: true });
      const user = await accountService.getProfile();

      setUserData({
        isFirstLoading: false,
        loading: false,
        profile: user?.data,
        isAuth: true,
      });
      retryCountRef.current = 0;
    } catch (error) {
      console.error('⚠️ Get profile error:', error);

      const storedToken = tokenStorage ?? getAuthenticationResult();
      const tokenExpired = storedToken?.ExpiryDate && moment(storedToken.ExpiryDate).isBefore(moment());

      if (!tokenExpired && retryCountRef.current < MAX_RETRY) {
        retryCountRef.current += 1;
        console.log(`🔁 Refreshing token (try ${retryCountRef.current})`);
        const newAccessToken = await onRefreshToken(storedToken?.refreshToken);

        if (newAccessToken) {
          const updatedTokenStorage = { ...storedToken, AccessToken: newAccessToken };
          setTimeout(() => getProfile(updatedTokenStorage), 200);
        } else {
          await onSignOut();
        }
      } else {
        await onSignOut();
      }
    }
  };

  // ================================
  // 📧 PASSWORD
  // ================================
  const onForgot = async (credentials: { email: string; frontendUrl?: string }): Promise<ResponseForgot> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.forgetPassword(credentials);
      setUserData({ loading: false });
      return response?.data;
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };

  const onVerify = async (credentials: { token: string }): Promise<ResponseReset> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.verifyToken(credentials);
      setUserData({ loading: false });
      return {
        email: response?.data?.data?.baseUser?.email,
        success: response?.data?.success,
        expiredAt: response?.data?.data?.expiredAt,
      };
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };

  const onResetPass = async (credentials: {
    email: string;
    password: string;
    token: string;
    frontendUrl?: string;
  }): Promise<ResponseReset> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.resetPassword(credentials);
      setUserData({ loading: false });
      return response?.data;
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };

  // ================================
  // ⏰ AUTO REFRESH
  // ================================
  useEffect(() => {
    if (!userData?.accessToken) return;
    const timer = setInterval(() => onRefreshToken(), 1000 * 60 * 60 * 12); // 12h
    return () => clearInterval(timer);
  }, [userData?.accessToken]);

  const fetchData = async () => {
    const tokenStorage = getAuthenticationResult();
    if (tokenStorage?.ExpiryDate && moment(tokenStorage.ExpiryDate).isBefore(moment())) {
      const newToken = await onRefreshToken();
      if (!newToken) return onSignOut();
    }
    await getProfile(tokenStorage);
  };

  // ================================
  // 🧭 GUARD LOGIC
  // ================================
  useEffect(() => {
    if (isRefreshing) return;
    if (userData?.accessToken && !userData?.profile) {
      fetchData();
      return;
    }
    if (!userData?.accessToken) {
      setUserData({ isFirstLoading: false });
      if (!currentPage?.isPublic) navigate('/login', { replace: true });
      return;
    }
    if (userData?.accessToken && userData?.profile) {
      if (currentPage?.isPublic && !currentPage.path?.includes('*')) navigate('/', { replace: true });
    }
  }, [userData?.accessToken, userData?.profile, currentPage, isRefreshing]);

  // ================================
  // 🔚 RETURN
  // ================================
  return (
    <AuthContext.Provider
      value={{
        onSignIn,
        onSignOut,
        getProfile,
        onRefreshToken,
        onForgot,
        onVerify,
        onResetPass,
        loading: userData.loading,
        accessToken: userData.accessToken,
        profile: userData.profile,
        isAuth: userData.isAuth,
      }}
    >
      {userData?.isFirstLoading || currentPage?.roles ? <LoadingFallback /> : children}
    </AuthContext.Provider>
  );
}

const useAuthContext = () => useContext(AuthContext);
export default useAuthContext;
